import axiosInstance from "../../utility/axios";

// state

import {
  setBlogs,
  setBlog,
  setComments,
  loadComments,
  setCommentsError,
  setLoadingBlog,
  disableLoadingBlog,
  setLoadingBlogId,
  updateBlog,
  replaceBlog,
  deleteBlog,
} from "../../store/slices/blog";
import { AppDispatch } from "../../store/store";
import {
  IBlog,
  IBlogResponse,
  ICommentResponse,
  ICreateBlog,
  ICreateBlogResponse,
  IEditBlog,
} from "../../store/types/blog";
import { toast } from "sonner";
import { getErrorMessage } from "../apiError";

const blogEndpoint = "/blog/api/v1";

export const get_blogs = async (
  dispatch: AppDispatch,
  limit: number = 20,
  offset: number = 0,
) => {
  dispatch(setLoadingBlog());
  try {
    const response = await axiosInstance.get<IBlogResponse>(
      `${blogEndpoint}/posts`,
      { params: { limit, offset } },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load blogs");
    }
    dispatch(setBlogs(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load blogs"));
    return false;
  } finally {
    dispatch(disableLoadingBlog());
  }
};

export const get_blog = async (dispatch: AppDispatch, id: number) => {
  dispatch(setLoadingBlog());
  try {
    const response = await axiosInstance.get<{
      success: boolean;
      message: string;
      data: IBlog;
    }>(`${blogEndpoint}/posts/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load blog");
    }
    dispatch(setBlog(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load blog"));
    return false;
  } finally {
    dispatch(disableLoadingBlog());
  }
};

const assertSuccess = (success: boolean, message: string, fallback: string) => {
  if (!success) {
    throw new Error(message || fallback);
  }
};

export const create_blog = async (
  form: ICreateBlog,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoadingBlog());
  try {
    const response = await axiosInstance.post<ICreateBlogResponse>(
      `${blogEndpoint}/posts`,
      form,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to create blog",
    );
    toast.success(response.data.message || "Blog created successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create blog"));
    return false;
  } finally {
    dispatch(disableLoadingBlog());
  }
};

export const edit_blog = async (
  form: IEditBlog,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoadingBlog());
  try {
    const { id, ...payload } = form;
    const blogId = Number(id);
    if (!Number.isInteger(blogId) || blogId <= 0) {
      throw new Error("A valid blog ID is required to update the blog");
    }
    const response = await axiosInstance.patch<ICreateBlogResponse>(
      `${blogEndpoint}/posts/${blogId}`,
      payload,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to update blog",
    );
    dispatch(updateBlog(form));
    toast.success(response.data.message || "Blog updated successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update blog"));
    return false;
  } finally {
    dispatch(disableLoadingBlog());
  }
};

export const destroy_blog = async (
  dispatch: AppDispatch,
  id: number,
): Promise<boolean> => {
  dispatch(setLoadingBlogId(id));
  try {
    const response = await axiosInstance.delete<{
      success: boolean;
      message: string;
    }>(`${blogEndpoint}/posts/${id}`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to delete blog",
    );
    dispatch(deleteBlog(id));
    toast.success(response.data.message || "Blog deleted successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to delete blog"));
    return false;
  } finally {
    dispatch(setLoadingBlogId(null));
  }
};

export const update_status = async (
  dispatch: AppDispatch,
  id: string,
): Promise<boolean> => {
  dispatch(setLoadingBlog());
  try {
    const response = await axiosInstance.patch<{
      success: boolean;
      message: string;
      data: IBlog;
    }>(`${blogEndpoint}/posts/${id}/status`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to update blog status",
    );
    dispatch(replaceBlog(response.data.data));
    toast.success(response.data.message || "Blog status updated");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update blog status"));
    return false;
  } finally {
    dispatch(disableLoadingBlog());
  }
};

export const get_post_comments = async (
  dispatch: AppDispatch,
  id: number,
  limit: number = 20,
  offset: number = 0,
): Promise<boolean> => {
  dispatch(loadComments(id));
  try {
    const response = await axiosInstance.get<ICommentResponse>(
      `${blogEndpoint}/posts/${id}/comments`,
      { params: { limit, offset } },
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to load blog comments",
    );
    dispatch(setComments({ blogId: id, data: response.data.data }));
    return true;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to load blog comments");
    dispatch(setCommentsError({ blogId: id, error: message }));
    toast.error(message);
    return false;
  }
};
